import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate, statusLabel } from "@/lib/format";
import { Plus, ArrowRight } from "lucide-react";

const nextActionStyle = (label) => {
  const map = {
    "Needs funding": "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    "Pending approval": "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
    "Ready to release": "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    "In dispute": "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    "Awaiting acceptance": "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    "In progress": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
    Released: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
    Cancelled: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  };
  return map[label] || "bg-secondary text-secondary-foreground";
};

const RecentContracts = ({ contracts, isAdmin, loading }) => (
  <div className="border bg-card border-border rounded-xl">
    <div className="flex items-center justify-between px-6 py-4 border-b border-border">
      <h2 className="text-xl font-semibold font-display">Recent contracts</h2>
      <Link
        to="/contracts"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        data-testid="view-all-contracts-link"
      >
        View all <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
    {loading && contracts.length === 0 ? (
      <div className="divide-y divide-border" data-testid="recent-contracts-skeleton">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="px-6 py-4">
            <div className="h-4 w-1/3 rounded bg-secondary animate-pulse" />
            <div className="mt-2 h-3 w-1/5 rounded bg-secondary/70 animate-pulse" />
          </div>
        ))}
      </div>
    ) : contracts.length === 0 ? (
      <div className="p-12 text-center">
        <div className="text-muted-foreground">No contracts yet.</div>
        {!isAdmin && (
          <Link to="/contracts/new" className="inline-block mt-4">
            <Button data-testid="empty-create-contract-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create your first contract
            </Button>
          </Link>
        )}
      </div>
    ) : (
      <div className="divide-y divide-border">
        {contracts.map((c) => (
          <Link
            key={c.contract_id}
            to={`/contracts/${c.contract_id}`}
            className="flex items-center justify-between px-6 py-4 transition-colors hover:bg-secondary/40"
            data-testid={`contract-row-${c.contract_id}`}
          >
            <div className="min-w-0">
              <div className="font-medium truncate">{c.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5 break-words">
                {c.is_payer ? `→ ${c.freelancer_name || c.freelancer_email}` : `← ${c.business_name}`}
                {" · "}
                {c.milestone_count} milestone{c.milestone_count === 1 ? "" : "s"}
                {(c.is_payer || c.is_payee) && !(c.is_payer && c.is_payee) && (
                  <span
                    className={`ml-2 px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-[0.15em] border ${c.is_payer ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-900/60" : "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-900/60"}`}
                  >
                    {c.is_payer ? "PAYER" : "PAYEE"}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="hidden text-right sm:block">
                <div className="font-semibold font-display">{fmtMoney(c.total_amount, c.currency)}</div>
                <div className="text-[10px] text-muted-foreground">{fmtDate(c.created_at)}</div>
              </div>
              <span
                className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${nextActionStyle(c.unified_status || c.next_action)}`}
                data-testid={`contract-status-${c.contract_id}`}
              >
                {c.unified_status || statusLabel(c.status)}
              </span>
            </div>
          </Link>
        ))}
      </div>
    )}
  </div>
);

export default RecentContracts;
